import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XMarkIcon, ArrowUpCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import TierBadge from './TierBadge';
import { registerModal } from '../../utils/modalStack';

// Route prefixes that require a paid feature — closing the modal on these
// routes should redirect to a safe page instead of leaving the user stranded.
const GATED_ROUTES = ['/shopify', '/craftflow'];

const UpgradePromptModal = () => {
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = e => setUpgradeInfo(e.detail);
    window.addEventListener('craftflow:upgrade-needed', handler);
    return () => window.removeEventListener('craftflow:upgrade-needed', handler);
  }, []);

  const handleClose = useCallback(() => {
    setUpgradeInfo(null);
    // A workspace dialog (the invoice builder) may be open on this route with unsaved work:
    // navigating away would unmount it and lose that work without its guard ever being asked,
    // and a plan failure is exactly when the seller wants to keep the form. Dismissing only
    // hides this prompt; the page's own gate still refuses the next call, so nothing is stranded.
    const workspaceOpen = typeof document !== 'undefined' && Boolean(document.querySelector('[data-workspace-dialog]'));
    // If currently on a gated route, redirect to a safe page
    const isOnGatedRoute = GATED_ROUTES.some(prefix => location.pathname.startsWith(prefix));
    if (isOnGatedRoute && !workspaceOpen) {
      navigate('/subscription', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleUpgrade = useCallback(() => {
    const tier = upgradeInfo?.required_tier;
    const query = tier ? `?plan=${tier}` : '';
    setUpgradeInfo(null);
    navigate(`/subscription${query}`);
  }, [upgradeInfo, navigate]);

  // Close on Escape through the overlay stack, not a window listener: the topmost overlay alone
  // answers Escape, so one keypress over an open WorkspaceDialog dismisses this prompt and the
  // dialog beneath never sees it. (A ref keeps the registration from churning with handleClose.)
  const closeRef = useRef(handleClose);
  closeRef.current = handleClose;
  const open = Boolean(upgradeInfo);
  useEffect(() => {
    if (!open) return undefined;
    return registerModal(() => closeRef.current()).unregister;
  }, [open]);

  if (!upgradeInfo) return null;

  const { message, required_tier, current_plan } = upgradeInfo;

  const tierDisplayName = tier => {
    if (!tier) return null;
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  // z-[60]: above WorkspaceDialog and Modal (z-50, and portalled after this tree, so equal
  // z-index would paint them over this prompt: a lapsed plan mid-session would raise it
  // invisibly), below ConfirmModal (z-[9999]). Same layer as the global toasts.
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in">
        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close upgrade prompt"
          className="absolute top-2 right-2 p-2 rounded-lg text-sage-400 hover:text-sage-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-14 h-14 bg-gradient-to-br from-lavender-100 dark:from-lavender-900/30 to-peach-100 dark:to-peach-900/30 rounded-2xl flex items-center justify-center">
            <SparklesIcon className="w-7 h-7 text-lavender-600 dark:text-lavender-300" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-lg font-semibold text-sage-900 dark:text-gray-100 text-center mb-2">Upgrade Required</h2>

        {/* Message */}
        <p className="text-sage-600 dark:text-gray-300 text-center mb-5">
          {message || 'This feature requires a higher subscription plan.'}
        </p>

        {/* Plan badges */}
        {(current_plan || required_tier) && (
          <div className="bg-sage-50 dark:bg-gray-900/50 rounded-xl p-4 mb-5 space-y-3">
            {current_plan && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-sage-700 dark:text-gray-300">Current Plan</span>
                <TierBadge tier={current_plan} size="small" />
              </div>
            )}
            {required_tier && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-sage-700 dark:text-gray-300">Required Plan</span>
                <TierBadge tier={required_tier} size="small" />
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleClose}
            className="flex-1 bg-sage-100 dark:bg-gray-700 text-sage-700 dark:text-gray-100 px-4 py-2.5 rounded-xl font-medium hover:bg-sage-200 dark:hover:bg-gray-600 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 bg-gradient-to-r from-lavender-500 to-lavender-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-lavender-600 hover:to-lavender-700 transition-all shadow-xs flex items-center justify-center space-x-2"
          >
            <ArrowUpCircleIcon className="w-5 h-5" />
            <span>{required_tier ? `Upgrade to ${tierDisplayName(required_tier)}` : 'View Plans'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePromptModal;
